import { useState } from 'react';
import { FonctionArmement, CompetenceFonctionArmement } from '../../../../shared/types/config';
import configService from '../../../../shared/services/configService';

interface UseFonctionArmementConfigParams {
  setError: (msg: string | null) => void;
  setSuccess: (msg: string | null) => void;
  loadAllData: () => Promise<void>;
}

export function useFonctionArmementConfig({
  setError,
  setSuccess,
  loadAllData,
}: UseFonctionArmementConfigParams) {
  const [fonctionsArmement, setFonctionsArmement] = useState<FonctionArmement[]>([]);
  const [searchFonctionsArmement, setSearchFonctionsArmement] = useState('');
  const [showFonctionArmementModal, setShowFonctionArmementModal] = useState(false);
  const [editingFonctionArmement, setEditingFonctionArmement] = useState<FonctionArmement | null>(null);
  const [fonctionArmementForm, setFonctionArmementForm] = useState<Omit<FonctionArmement, 'id'>>({
    nom: '',
    description: '',
    id_competences: [],
  });

  const handleAddFonctionArmement = () => {
    setEditingFonctionArmement(null);
    setFonctionArmementForm({
      nom: '',
      description: '',
      id_competences: [],
    });
    setShowFonctionArmementModal(true);
  };

  const handleEditFonctionArmement = (fonctionArmement: FonctionArmement) => {
    setEditingFonctionArmement(fonctionArmement);
    let formattedCompetences: CompetenceFonctionArmement[] = [];
    if (fonctionArmement.id_competences) {
      if (Array.isArray(fonctionArmement.id_competences)) {
        if (fonctionArmement.id_competences.length > 0) {
          const firstItem = fonctionArmement.id_competences[0];
          if (typeof firstItem === 'number') {
            formattedCompetences = (fonctionArmement.id_competences as unknown as number[]).map((id, index) => ({
              id,
              ordre: index + 1,
              optionnel: false,
              ref: false,
            }));
          } else if (typeof firstItem === 'object' && 'id' in firstItem && 'ordre' in firstItem) {
            formattedCompetences = fonctionArmement.id_competences.map((c) => ({
              ...c,
              optionnel: c.optionnel || false,
              ref: c.ref || false,
            }));
          }
        }
      }
    }
    setFonctionArmementForm({
      nom: fonctionArmement.nom,
      description: fonctionArmement.description || '',
      id_competences: formattedCompetences,
    });
    setShowFonctionArmementModal(true);
  };

  const handleSaveFonctionArmement = async () => {
    try {
      setError(null);
      let result;
      if (editingFonctionArmement?.id) {
        result = await configService.updateFonctionArmement(editingFonctionArmement.id, fonctionArmementForm);
      } else {
        result = await configService.createFonctionArmement(fonctionArmementForm);
      }
      if (result.success) {
        setSuccess(editingFonctionArmement
          ? 'Fonction d\'armement modifiée avec succès'
          : 'Fonction d\'armement ajoutée avec succès');
        setShowFonctionArmementModal(false);
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteFonctionArmement = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette fonction d\'armement ?')) return;
    try {
      setError(null);
      const result = await configService.deleteFonctionArmement(id);
      if (result.success) {
        setSuccess('Fonction d\'armement supprimée avec succès');
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la suppression');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };

  return {
    fonctionsArmement,
    setFonctionsArmement,
    searchFonctionsArmement,
    setSearchFonctionsArmement,
    showFonctionArmementModal,
    setShowFonctionArmementModal,
    editingFonctionArmement,
    fonctionArmementForm,
    setFonctionArmementForm,
    handleAddFonctionArmement,
    handleEditFonctionArmement,
    handleSaveFonctionArmement,
    handleDeleteFonctionArmement,
  };
}
